<template>
    <v-container fluid>
        <page-header title="Teams" icon="mdi-account-group-outline">
            <template v-slot:actions>
                <registration />
                <v-btn v-if="csvEnabled" class="white--text" depressed small color="black" @click="downloadTeamsCSV">
                    <v-icon left small>mdi-download</v-icon>Export CSV
                </v-btn>
                <v-btn v-if="csvEnabled" class="white--text" depressed small color="black" @click="showImportDialog = true">
                    <v-icon left small>mdi-upload</v-icon>Upload CSV
                </v-btn>
                <v-btn class="white--text" depressed small color="black" @click="createTeam">
                    <v-icon left small>mdi-plus</v-icon>Create
                </v-btn>
            </template>
            <table-filters :fields="filterFields" :value="filterValues" @apply="applyFilters" />
        </page-header>
        <import-dialog
            :open-dialog="showImportDialog"
            entity-label="Teams CSV"
            :successfull-import="successfullImport"
            file-type=".csv,text/csv"
            :message="message"
            @file-import="uploadTeams"
            @close-import-dialog="closeImportDialog"
        />
        <array-view
            :items="teams"
            :headers="displayInfo"
            :total-pages="totalPages"
            :page="page"
            :size="size"
            :sort-by="sort.sortBy"
            :sort-desc="sort.sortDesc"
            @handleClick="handleClick"
            @paginationChange="paginationChange"
            @sortChange="changeSort"
            @onDelete="onDelete"
        />
    </v-container>
</template>

<script>
    import teamService from '@/services/team'
    import ArrayView from '@/components/ArrayView'
    import Registration from '@/forms/Registration'
    import leagueService from '@/services/league'
    import cupService from '@/services/cup'
    import TableFilters from '@/components/TableFilters'
    import PageHeader from '@/components/PageHeader'
    import listTable from '@/mixins/listTable'
    import ImportDialog from '@/components/ImportDialog'

    export default {
        name: 'Team',
        components: { ArrayView, Registration, TableFilters, ImportDialog, PageHeader },
        mixins: [listTable],
        data: () => {
            return {
                successfullImport: null,
                teams: [],
                displayInfo: [
                    {
                        text: 'Id',
                        value: 'id'
                    },
                    {
                        text: 'Name',
                        value: 'name'
                    },
                    {
                        // a team can play in more than one league
                        text: 'Leagues',
                        value: 'leagues',
                        sortable: false
                    },
                    {
                        text: 'Cup League',
                        value: 'cupLeague',
                        subLevel: 'title'
                    },
                    {
                        text: 'Active',
                        value: 'active'
                    },
                    {
                        // paid or not for the season running today (no row for the season: not paid)
                        text: 'Subscription',
                        value: 'subscriptionPaid',
                        sortable: false,
                        chip: team => (team.subscriptionPaid === null || team.subscriptionPaid === undefined
                            ? null
                            : {
                                text: team.subscriptionPaid ? 'Paid' : 'Unpaid',
                                color: team.subscriptionPaid ? 'green darken-1' : 'red darken-1',
                                icon: team.subscriptionPaid ? 'mdi-check-circle' : 'mdi-alert-circle-outline',
                                title: `Subscription ${team.subscriptionSeason || ''}`.trim()
                            })
                    }
                ],
                tableKey: 'teams',
                filterFields: [
                    { key: 'name', label: 'Name', type: 'text' },
                    { key: 'nickName', label: 'Nickname', type: 'text' },
                    { key: 'leagues', label: 'League', type: 'autocomplete', itemText: 'title', loadItems: () => leagueService.getAllLeagues().then(resp => resp.data) },
                    { key: 'cupLeague', label: 'Cup League', type: 'autocomplete', itemText: 'title', loadItems: () => cupService.getAllCups().then(resp => resp.data) },
                    { key: 'active', label: 'Active', type: 'boolean', default: true }
                ],
                showImportDialog: false,
                csvEnabled: true,
                size: 100,
                totalPages: 0,
                page: 1,
                search: null,
                loading: false,
                isActive: false,
                message: null
            }
        },
        created() {
            this.fetchItems()
        },
        methods: {
            downloadTeamsCSV() {
                teamService.downloadTeamsCSV(this.filterQuery).then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', 'teams.csv')
                    document.body.appendChild(link)
                    link.click()
                })
            },
            closeImportDialog() {
                this.showImportDialog = false
                // reset state so dialog shows proper buttons next time
                this.successfullImport = null
            },
            uploadTeams(file) {
                this.successfullImport = null
                const formData = new FormData()
                formData.append('file', file)
                teamService.uploadTeamsCSV(formData).then(() => {
                    this.successfullImport = true
                    this.fetchItems()
                    this.closeImportDialog()
                }).catch((error) => {
                    this.successfullImport = false
                    this.message = error.response.data.message || 'Import failed'
                })
            },
            resetPage() {
                this.paginationChange(1, this.size)
            },
            handleClick(action, item) {
                this.$router.push(`/teams/${item.id}/${action}`)
            },
            createTeam() {
                this.$router.push(`/teams/create`)
            },
            fetchItems() {
                this.loading = true
                teamService.getFilteredTeams(this.filterQuery, this.page, this.size, this.sort).then(resp => {
                    this.teams = resp.data.content
                    this.totalPages = resp.data.totalPages
                    this.loading = false
                })
            },
            onDelete(item, page) {
                teamService.deleteTeam(item.id).then(() => {
                    this.page = page
                    this.fetchItems()
                })
            }
        }
    }
</script>

<style scoped>

.v-list-item {
    min-height: 30px !important;
}

.v-card {
    min-width: 250px;
    position: relative;
}
.edit-button:hover {
    background-color: whitesmoke;
    color: white;
}

.screen-card {
    padding: 4%;
}

.overflow-text {
    max-width: 79%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.pagination {
    justify-content: center;
}
</style>
